import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const PaywallContext = createContext({
  open: false,
  reason: null,
  openPaywall: () => {},
  closePaywall: () => {},
  handle402: () => false,
});

export function PaywallProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(null);

  const openPaywall = useCallback((why) => {
    setReason(why || null);
    setOpen(true);
  }, []);

  const closePaywall = useCallback(() => {
    setOpen(false);
    setReason(null);
  }, []);

  // Auth listener (in lib/auth.js) dispatches `tiplingo:close-paywall` after
  // a successful sign-in so the user lands on the home screen, not a stale
  // limit-reached Paywall.
  useEffect(() => {
    const onClose = () => {
      setOpen(false);
      setReason(null);
    };
    window.addEventListener("tiplingo:close-paywall", onClose);
    return () => window.removeEventListener("tiplingo:close-paywall", onClose);
  }, []);

  // if an axios error is a 402 limit_reached, open paywall and return true
  const handle402 = useCallback(
    (err) => {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail?.error === "limit_reached") {
        openPaywall(detail.kind);
        return true;
      }
      return false;
    },
    [openPaywall]
  );

  const value = useMemo(
    () => ({ open, reason, openPaywall, closePaywall, handle402 }),
    [open, reason, openPaywall, closePaywall, handle402]
  );

  return <PaywallContext.Provider value={value}>{children}</PaywallContext.Provider>;
}

export function usePaywall() {
  return useContext(PaywallContext);
}
